import { Edit, Group } from '@mui/icons-material';
import { Box, IconButton, Paper, Skeleton, Typography } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import { getMissingApprovals } from 'lib/api/approvals';
import { validatePermissions } from 'lib/functions/utils';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { useComiteeTabContext } from './ComiteeTabProvider';
import { CommitteeInfoCardProps } from './types';

const CommitteeInfoCardSkeleton = () => {
  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        position: 'relative',
        borderRadius: '8px',
      }}
    >
      <Skeleton
        variant="circular"
        width={32}
        height={32}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Skeleton
          variant="circular"
          width={40}
          height={40}
          sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)' }}
        />
        <Box>
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={120} height={16} />
        </Box>
      </Box>

      {/* Description skeleton */}
      <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="85%" height={16} />
    </Paper>
  );
};

const CommitteeInfoCard = ({ onEditClick }: CommitteeInfoCardProps) => {
  const { requestType, requestId, comiteeType, canEdit } =
    useComiteeTabContext();

  const dispatch = useDispatch();

  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const canChangeCommittee =
    canEdit && validatePermissions(user, 'comitee_change');

  const { data: missingApprovals, isPending } = getMissingApprovals(
    dispatch,
    requestType,
    requestId,
    comiteeType,
  );

  const comiteeName = missingApprovals?.comiteeName;
  const mainColor = comiteeName ? 'primary.main' : 'warning.main';
  const backgroundColor = comiteeName
    ? 'rgba(25, 118, 210, 0.08)'
    : 'rgba(255, 152, 0, 0.08)';

  const isUpdating = useIsMutating({
    mutationKey: [
      'approvals',
      'updateComitee',
      requestType,
      requestId,
      comiteeType,
    ],
  });

  if (isPending || isUpdating) return <CommitteeInfoCardSkeleton />;

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        position: 'relative',
        borderRadius: '8px',
      }}
    >
      {canChangeCommittee && (
        <IconButton
          onClick={onEditClick}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: backgroundColor,
            '&:hover': {
              backgroundColor: backgroundColor,
            },
          }}
          size="small"
        >
          <Edit
            sx={{
              fontSize: '1rem',
              color: mainColor,
            }}
          />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box
          sx={{
            backgroundColor: backgroundColor,
            borderRadius: '50%',
            p: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Group
            sx={{
              fontSize: '1rem',
              color: mainColor,
            }}
          />
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              lineHeight: 1,
              fontSize: '1.1rem',
              color: mainColor,
            }}
          >
            {comiteeName ? comiteeName : 'Sin comité asignado'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.8rem',
              lineHeight: 1,
            }}
          >
            {comiteeName ? 'Comité de Aprobación' : 'Estado del comité'}
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', lineHeight: 1.4 }}
      >
        {comiteeName
          ? 'Este comité es responsable de revisar y aprobar las solicitudes según los criterios establecidos. Cada rol debe ser aprobado por los usuarios correspondientes.'
          : 'No se ha asignado un comité a esta solicitud. Una vez asignado, podrás ver los roles requeridos y el estado de las aprobaciones. El comité será asignado automáticamente según los criterios de la solicitud y los roles disponibles en el sistema.'}
      </Typography>
    </Paper>
  );
};

export default CommitteeInfoCard;
